import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";

import CertificateService from "../../services/certificateServices";

const CertificateAdminList = () => {
  const [certificateAdmins, setCertificateAdmins] = useState([]);

  const [loading, setLoading] = useState(false);

  // Search / Filter
  const [searchType, setSearchType] = useState("name");
  const [searchValue, setSearchValue] = useState("");
  const [status, setStatus] = useState("");

  // Edit Dialog
  const [editOpen, setEditOpen] = useState(false);

  const [editData, setEditData] = useState({
    user_id: "",
    name: "",
    email: "",
    mobile: "",
    address: "",
    address_pin: "",
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // --------------------------------------------------
  // Snackbar
  // --------------------------------------------------

  const showMessage = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // --------------------------------------------------
  // Get Certificate Admin List
  // --------------------------------------------------

  const fetchCertificateAdmins = async (customParams = null) => {
    try {
      setLoading(true);

      const params =
        customParams !== null
          ? customParams
          : {};

      if (customParams === null) {
        if (searchValue.trim()) {
          params[searchType] = searchValue.trim();
        }

        if (status) {
          params.status = status;
        }
      }

      const response =
        await CertificateService.getCertificateAdminList(params);

      const data = response?.data;

      setCertificateAdmins(data?.results || []);
    } catch (error) {
      console.error(
        "Certificate Admin list error:",
        error
      );

      const backendMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to fetch certificate admin list.";

      showMessage(backendMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // Initial API Call
  // --------------------------------------------------

  useEffect(() => {
    fetchCertificateAdmins();
  }, []);

  // --------------------------------------------------
  // Search
  // --------------------------------------------------

  const handleSearch = () => {
    fetchCertificateAdmins();
  };

  // --------------------------------------------------
  // Clear Filters
  // --------------------------------------------------

  const handleClearFilters = () => {
    setSearchType("name");
    setSearchValue("");
    setStatus("");

    fetchCertificateAdmins({});
  };

  // --------------------------------------------------
  // Edit Certificate Admin
  // --------------------------------------------------

  const handleEdit = (certificateAdmin) => {
    setEditData({
      user_id:
        certificateAdmin.user_id ||
        certificateAdmin.id ||
        "",
      name: certificateAdmin.name || "",
      email: certificateAdmin.email || "",
      mobile: certificateAdmin.mobile || "",
      address: certificateAdmin.address || "",
      address_pin:
        certificateAdmin.address_pin || "",
    });

    setEditOpen(true);
  };

  // --------------------------------------------------
  // Edit Field Change
  // --------------------------------------------------

  const handleEditChange = (event) => {
    const { name, value } = event.target;

    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // --------------------------------------------------
  // Update Certificate Admin
  // --------------------------------------------------

  const handleUpdate = async () => {
    try {
      setLoading(true);

      const payload = {
        user_id: editData.user_id,
        name: editData.name,
        email: editData.email,
        mobile: editData.mobile,
        address: editData.address,
        address_pin: editData.address_pin,
      };

      const response =
        await CertificateService.updateCertificateAdmin(
          payload
        );

      showMessage(
        response?.data?.message ||
          "Certificate admin updated successfully.",
        "success"
      );

      setEditOpen(false);

      // Refresh list after update
      await fetchCertificateAdmins();
    } catch (error) {
      console.error(
        "Certificate Admin update error:",
        error
      );

      const backendMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to update certificate admin.";

      showMessage(backendMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // Render
  // --------------------------------------------------

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={600}
          >
            Certificate Admin List
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
          >
            Manage and update certificate admins
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() =>
            fetchCertificateAdmins()
          }
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filter Section */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ mb: 2 }}
        >
          Filter Certificate Admins
        </Typography>

        <Grid container spacing={2}>
          {/* Search Type */}
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Search By"
              value={searchType}
              onChange={(e) =>
                setSearchType(e.target.value)
              }
            >
              <MenuItem value="name">
                Name
              </MenuItem>

              <MenuItem value="email">
                Email
              </MenuItem>

              <MenuItem value="mobile">
                Mobile
              </MenuItem>
            </TextField>
          </Grid>

          {/* Search */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={`Search ${searchType}`}
              value={searchValue}
              onChange={(e) =>
                setSearchValue(e.target.value)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value)
              }
            >
              <MenuItem value="">
                All
              </MenuItem>

              <MenuItem value="active">
                Active
              </MenuItem>

              <MenuItem value="deactive">
                Deactive
              </MenuItem>
            </TextField>
          </Grid>

          {/* Search Button */}
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{ height: 40 }}
            >
              {loading ? (
                <CircularProgress
                  size={22}
                  color="inherit"
                />
              ) : (
                "Search"
              )}
            </Button>
          </Grid>

          {/* Clear Filters */}
          <Grid item xs={12}>
            <Button
              variant="text"
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Certificate Admin Table */}
      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box sx={{ overflowX: "auto" }}>
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 900,

              "& th": {
                backgroundColor: "#f5f5f5",
                fontWeight: 600,
                textAlign: "left",
                padding: "14px 16px",
                borderBottom:
                  "1px solid #ddd",
                whiteSpace: "nowrap",
              },

              "& td": {
                padding: "13px 16px",
                borderBottom:
                  "1px solid #eee",
              },

              "& tbody tr:hover": {
                backgroundColor: "#fafafa",
              },
            }}
          >
            <thead>
              <tr>
                <th>Sl. No.</th>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Address</th>
                <th>PIN</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {certificateAdmins.length > 0 ? (
                certificateAdmins.map(
                  (admin, index) => (
                    <tr
                      key={
                        admin.user_id ||
                        admin.id ||
                        index
                      }
                    >
                      <td>{index + 1}</td>

                      <td>
                        {admin.name || "-"}
                      </td>

                      <td>
                        {admin.email || "-"}
                      </td>

                      <td>
                        {admin.mobile || "-"}
                      </td>

                      <td>
                        {admin.address || "-"}
                      </td>

                      <td>
                        {admin.address_pin || "-"}
                      </td>

                      <td>
                        <Typography
                          component="span"
                          sx={{
                            display:
                              "inline-block",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 10,
                            fontSize: 12,
                            fontWeight: 600,

                            backgroundColor:
                              admin.status ===
                              "active"
                                ? "#e8f5e9"
                                : "#ffebee",

                            color:
                              admin.status ===
                              "active"
                                ? "#2e7d32"
                                : "#c62828",
                          }}
                        >
                          {admin.status || "-"}
                        </Typography>
                      </td>

                      {/* Action */}
                      <td>
                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleEdit(admin)
                          }
                          title="Edit Certificate Admin"
                        >
                          <EditIcon />
                        </IconButton>
                      </td>
                    </tr>
                  )
                )
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    style={{
                      textAlign: "center",
                      padding: "30px",
                    }}
                  >
                    {loading
                      ? "Loading certificate admins..."
                      : "No certificate admins found."}
                  </td>
                </tr>
              )}
            </tbody>
          </Box>
        </Box>
      </Paper>

      {/* Edit Certificate Admin Dialog */}
      <Dialog
        open={editOpen}
        onClose={() =>
          setEditOpen(false)
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Edit Certificate Admin
        </DialogTitle>

        <DialogContent>
          <Grid
            container
            spacing={2}
            sx={{ mt: 0.5 }}
          >
            {/* Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={editData.name}
                onChange={handleEditChange}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={editData.email}
                onChange={handleEditChange}
              />
            </Grid>

            {/* Mobile */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile"
                name="mobile"
                value={editData.mobile}
                onChange={(e) => {
                  const value =
                    e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);

                  setEditData((prev) => ({
                    ...prev,
                    mobile: value,
                  }));
                }}
                inputProps={{
                  maxLength: 10,
                }}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Address"
                name="address"
                value={editData.address}
                onChange={handleEditChange}
              />
            </Grid>

            {/* Address PIN */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Address PIN"
                name="address_pin"
                value={
                  editData.address_pin
                }
                onChange={(e) => {
                  const value =
                    e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 6);

                  setEditData((prev) => ({
                    ...prev,
                    address_pin: value,
                  }));
                }}
                inputProps={{
                  maxLength: 6,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2,
          }}
        >
          <Button
            onClick={() =>
              setEditOpen(false)
            }
            variant="outlined"
          >
            Cancel
          </Button>

          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress
                size={22}
                color="inherit"
              />
            ) : (
              "Update"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CertificateAdminList;